import { normalizeTutorialUrls } from './tutorialUrls';

export const PROTOCOL_OPTIONS = [
  { value: 'gpio', label: 'GPIO' },
  { value: 'analog', label: 'Analog / ADC' },
  { value: 'pwm', label: 'PWM' },
  { value: 'i2c', label: 'I2C' },
  { value: 'i3c', label: 'I3C' },
  { value: 'spi', label: 'SPI' },
  { value: 'qspi', label: 'QSPI' },
  { value: 'ospi', label: 'OSPI' },
  { value: 'uart', label: 'UART / USART' },
  { value: 'rs-232', label: 'RS-232' },
  { value: 'rs-485', label: 'RS-485' },
  { value: 'rs-422', label: 'RS-422' },
  { value: 'lin', label: 'LIN' },
  { value: 'one-wire', label: '1-Wire' },
  { value: 'can', label: 'CAN' },
  { value: 'can-fd', label: 'CAN FD' },
  { value: 'i2s', label: 'I2S' },
  { value: 'pdm', label: 'PDM' },
  { value: 'usb', label: 'USB' },
  { value: 'sdio', label: 'SDIO' },
  { value: 'ethernet', label: 'Ethernet' },
  { value: 'ble', label: 'BLE' },
  { value: 'bluetooth-classic', label: 'Bluetooth Classic' },
  { value: 'wifi', label: 'WiFi' },
  { value: 'pio', label: 'PIO' },
  { value: 'hstx', label: 'HSTX' },
  { value: 'swd', label: 'SWD' },
  { value: 'jtag', label: 'JTAG' },
  { value: 'modbus', label: 'Modbus' }
];

const PROTOCOL_ALIAS_BY_LOOKUP_KEY = {
  gpio: 'gpio',
  digitalio: 'gpio',
  dio: 'gpio',
  adc: 'analog',
  analog: 'analog',
  analoginput: 'analog',
  analogin: 'analog',
  dac: 'analog',
  pwm: 'pwm',
  i2c: 'i2c',
  iic: 'i2c',
  twi: 'i2c',
  twowire: 'i2c',
  i3c: 'i3c',
  spi: 'spi',
  qspi: 'qspi',
  quadspi: 'qspi',
  ospi: 'ospi',
  octospi: 'ospi',
  octalspi: 'ospi',
  uart: 'uart',
  usart: 'uart',
  serial: 'uart',
  ttlserial: 'uart',
  rs232: 'rs-232',
  rs485: 'rs-485',
  rs422: 'rs-422',
  lin: 'lin',
  '1wire': 'one-wire',
  onewire: 'one-wire',
  wire1: 'one-wire',
  can: 'can',
  canbus: 'can',
  canfd: 'can-fd',
  i2s: 'i2s',
  iis: 'i2s',
  pdm: 'pdm',
  usb: 'usb',
  usb11: 'usb',
  usb10: 'usb',
  usb20: 'usb',
  usbc: 'usb',
  nativeusb: 'usb',
  sdio: 'sdio',
  sd: 'sdio',
  ethernet: 'ethernet',
  eth: 'ethernet',
  ble: 'ble',
  bluetoothle: 'ble',
  bluetoothlowenergy: 'ble',
  btle: 'ble',
  bluetooth: 'bluetooth-classic',
  bluetoothclassic: 'bluetooth-classic',
  bluetoothlegacy: 'bluetooth-classic',
  legacybluetooth: 'bluetooth-classic',
  bredr: 'bluetooth-classic',
  br: 'bluetooth-classic',
  edr: 'bluetooth-classic',
  wifi: 'wifi',
  wirelesslan: 'wifi',
  wlan: 'wifi',
  pio: 'pio',
  programmableio: 'pio',
  programmableinputoutput: 'pio',
  hstx: 'hstx',
  highspeedtx: 'hstx',
  highspeedtransmit: 'hstx',
  swd: 'swd',
  serialwiredebug: 'swd',
  jtag: 'jtag',
  modbus: 'modbus',
  modbusrtu: 'modbus'
};

const PROTOCOL_OPTION_VALUES = new Set(PROTOCOL_OPTIONS.map((option) => option.value));

const normalizeProtocolLookupKey = (value) => String(value ?? '')
  .trim()
  .toLowerCase()
  .replace(/\+/g, 'plus')
  .replace(/&/g, 'and')
  .replace(/[^a-z0-9]/g, '');

export const normalizeProtocolValue = (value) => {
  const text = String(value ?? '').trim();
  if (!text) {
    return '';
  }

  const lookupKey = normalizeProtocolLookupKey(text);
  if (PROTOCOL_ALIAS_BY_LOOKUP_KEY[lookupKey]) {
    return PROTOCOL_ALIAS_BY_LOOKUP_KEY[lookupKey];
  }

  const normalizedValue = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return PROTOCOL_OPTION_VALUES.has(normalizedValue) ? normalizedValue : normalizedValue;
};

export const PIN_ROLE_OPTIONS = [
  { value: '', label: 'Select a primary pin role...' },
  { value: 'power-input', label: 'Power Input' },
  { value: 'power-output-3v3', label: '3.3V Output' },
  { value: 'power-output-5v', label: '5V Output' },
  { value: 'ground', label: 'Ground' },
  { value: 'gpio', label: 'GPIO' },
  { value: 'analog-in', label: 'Analog Input' },
  { value: 'analog-out', label: 'Analog Output' },
  { value: 'pwm', label: 'PWM' },
  { value: 'i2c-sda', label: 'I2C SDA' },
  { value: 'i2c-scl', label: 'I2C SCL' },
  { value: 'spi-mosi', label: 'SPI MOSI' },
  { value: 'spi-miso', label: 'SPI MISO' },
  { value: 'spi-sck', label: 'SPI SCK' },
  { value: 'spi-cs', label: 'SPI CS' },
  { value: 'uart-tx', label: 'UART TX' },
  { value: 'uart-rx', label: 'UART RX' },
  { value: 'interrupt', label: 'Interrupt' },
  { value: 'control', label: 'Control' },
  { value: 'chip-enable', label: 'Chip Enable' },
  { value: 'other', label: 'Other' }
];

export const SUGGESTED_PIN_CAPABILITY_TAGS = [
  'gpio',
  'digital',
  'pio',
  'analog-in',
  'analog-out',
  'pwm',
  'interrupt',
  'power-3v3',
  'power-5v',
  'power-vin',
  'ground',
  'i2c-sda',
  'i2c-scl',
  'spi-mosi',
  'spi-miso',
  'spi-sck',
  'spi-cs',
  'uart-tx',
  'uart-rx',
  'chip-select',
  'control'
];

export const BOARD_VERIFICATION_STATUS_OPTIONS = [
  { value: 'unverified', label: 'Unverified' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'verified', label: 'Verified' }
];

const BOARD_VERIFICATION_STATUS_LABELS = {
  unverified: 'Unverified',
  reviewed: 'Reviewed',
  verified: 'Verified'
};

const BOARD_VERIFICATION_BADGE_LABELS = {
  unverified: 'Unverified pinout',
  reviewed: 'Reviewed pinout',
  verified: 'Verified pinout'
};

export const normalizeStringArray = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item ?? '').trim())
      .filter(Boolean)
      .filter((item, index, array) => array.indexOf(item) === index);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return [];
    }

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return normalizeStringArray(parsed);
      }
    } catch (error) {
      // Fall back to comma-separated parsing.
    }

    return normalizeStringArray(trimmed.split(','));
  }

  return [];
};

export const normalizeSupportedProtocols = (value) => normalizeStringArray(value)
  .map(normalizeProtocolValue)
  .filter(Boolean)
  .filter((item, index, array) => array.indexOf(item) === index);

export const normalizeBooleanFlag = (value) => (
  value === true
  || value === 1
  || value === '1'
  || String(value ?? '').trim().toLowerCase() === 'true'
);

export const toggleStringArrayValue = (currentValues, nextValue) => {
  const normalizedValues = normalizeStringArray(currentValues);

  if (normalizedValues.includes(nextValue)) {
    return normalizedValues.filter((value) => value !== nextValue);
  }

  return [...normalizedValues, nextValue];
};

export const toggleSupportedProtocolValue = (currentValues, nextValue) => {
  const normalizedValues = normalizeSupportedProtocols(currentValues);
  const normalizedNextValue = normalizeProtocolValue(nextValue);

  if (!normalizedNextValue) {
    return normalizedValues;
  }

  if (normalizedValues.includes(normalizedNextValue)) {
    return normalizedValues.filter((value) => value !== normalizedNextValue);
  }

  return [...normalizedValues, normalizedNextValue];
};

export const formatTagListForInput = (value) => normalizeStringArray(value).join(', ');

export const parseTagListInput = (value) => normalizeStringArray(value);

export const normalizeBoardVerificationStatus = (value) => {
  const normalizedValue = String(value ?? '').trim().toLowerCase();

  if (normalizedValue === 'reviewed' || normalizedValue === 'verified') {
    return normalizedValue;
  }

  return 'unverified';
};

export const getBoardVerificationLabel = (value) => (
  BOARD_VERIFICATION_STATUS_LABELS[normalizeBoardVerificationStatus(value)]
);

export const getBoardVerificationBadgeLabel = (value) => (
  BOARD_VERIFICATION_BADGE_LABELS[normalizeBoardVerificationStatus(value)]
);

export const createEmptyBoardCompatibilityFields = () => ({
  supply_voltage_min_v: '',
  supply_voltage_max_v: '',
  logic_voltage_min_v: '',
  logic_voltage_max_v: '',
  current_draw_typ_ma: '',
  current_draw_max_ma: '',
  rail_3v3_max_ma: '',
  rail_5v_max_ma: '',
  supported_protocols: [],
  protocol_notes: ''
});

export const createEmptyPinCompatibilityFields = () => ({
  pin_role: '',
  voltage_min_v: '',
  voltage_max_v: '',
  current_limit_ma: '',
  capability_tags: [],
  is_5v_tolerant: false,
  use_with_caution: false,
  caution_reason: ''
});

export const withBoardCompatibilityDefaults = (board = {}) => ({
  ...createEmptyBoardCompatibilityFields(),
  ...board,
  datasheet_url: String(board?.datasheet_url ?? '').trim(),
  tutorial_urls: normalizeTutorialUrls(board?.tutorial_urls),
  verification_status: normalizeBoardVerificationStatus(board?.verification_status),
  supported_protocols: normalizeSupportedProtocols(board?.supported_protocols)
});

export const withPinCompatibilityDefaults = (pin = {}) => ({
  ...createEmptyPinCompatibilityFields(),
  ...pin,
  capability_tags: normalizeStringArray(pin?.capability_tags),
  is_5v_tolerant: normalizeBooleanFlag(pin?.is_5v_tolerant),
  use_with_caution: normalizeBooleanFlag(pin?.use_with_caution),
  caution_reason: String(pin?.caution_reason ?? '').trim()
});
